import { useNavigate } from 'react-router-dom';

import {
  ComplianceTypes,
  LUMIQ_INTERNAL_ORGANIZATION_NAME,
  Modals,
  NEW_FEATURE_ALERTS,
  Routes,
} from '@/constants';
import { useBookmarkManagement, useNewFeatureAlert } from '@/hooks';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  playerSessionsSelectors,
  playerThunks,
  selectActiveChapterId,
  selectEpisodeCompliance,
  selectIsEpisodePlaying,
  selectIsEpisodeRedeemRequired,
  selectIsFreeTrialUser,
  selectPlan,
  selectSessionUserId,
  uiActions,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { Episode, Referrer } from '@/types';
import { createRouteWithParams, SnackbarUtils } from '@/utils';

import { EpisodeControlButtons as EpisodeControlButtonsComponent } from './EpisodeControlButtons.component';

export const EpisodeControlButtons = ({
  episode,
  referrer,
}: {
  episode: Episode;
  referrer: Referrer;
}) => {
  const { episodeId } = episode;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, episodeId),
  );

  const userId = useAppSelector(selectSessionUserId);
  const { data: plan } = useAppSelector(selectPlan);

  const isUserInternal =
    plan?.organizationName === LUMIQ_INTERNAL_ORGANIZATION_NAME;

  const isFreeTrialUser = useAppSelector(selectIsFreeTrialUser);
  const isEpisodeRedeemRequired = useAppSelector(state =>
    selectIsEpisodeRedeemRequired(state, episodeId),
  );
  const playerSession = useAppSelector(state =>
    playerSessionsSelectors.selectById(state, episodeId),
  );
  const episodeStatus = playerSession?.status;

  const newFeatureAlert = useNewFeatureAlert(
    NEW_FEATURE_ALERTS.SHARE_EPISODE_MODAL,
  );
  const isEpisodePlaying = useAppSelector(state =>
    selectIsEpisodePlaying(state, episode.episodeId),
  );
  const activeChapterId = useAppSelector(selectActiveChapterId);

  const {
    toggleBookmark: onToggleBookmark,
    isBookmarking,
    isBookmarked,
  } = useBookmarkManagement({
    episodeId,
    location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.BOOKMARK },
    referrer,
  });

  if (
    episode.isArchived ||
    (compliance === ComplianceTypes.CPE &&
      episode.compliance !== ComplianceTypes.CPE)
  )
    return null;

  const onRedeem = () => {
    dispatch(
      playerThunks.initiateRedemption({
        episodeId,
        location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.REDEEM },
      }),
    );
  };

  const onPlayNow = () => {
    dispatch(
      playerThunks.episodePlayPressed({
        episodeId,
        location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.PLAY_NOW },
        referrer,
      }),
    );
  };

  const onTrailer = () => {
    dispatch(
      playerThunks.chapterPlayPressed({
        activeChapter: {
          episodeId,
          chapterId: episode.trailerId,
        },
        location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.PREVIEW },
        referrer,
      }),
    );
  };

  const onTakeAssessment = () => {
    if (isEpisodeRedeemRequired) {
      dispatch(
        playerThunks.initiateRedemption({
          episodeId,
          location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.ASSESSMENT },
        }),
      );
      return;
    }
    const assessmentUrl = createRouteWithParams(Routes.EPISODE_ASSESSMENT, {
      episodeId,
    });
    navigate(assessmentUrl);
  };

  const onGiveFeedback = () => {
    dispatch(
      uiActions.setActiveModal({
        name: Modals.EPISODE_EVALUATION,
        params: { episodeId },
      }),
    );
  };

  const onShare = () => {
    if (userId) {
      dispatch(
        uiActions.setActiveModal({
          name: Modals.SHARE_EPISODE,
          params: { episodeId, referrer },
        }),
      );
    } else {
      navigator.clipboard.writeText(episode.deepLink);
      AnalyticsService.episodeShareCopy({
        episodeId,
        location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.SHARE },
      });
      SnackbarUtils.success('Link copied to clipboard');
    }
  };

  const onCopyLink = () => {
    navigator.clipboard.writeText(episode.deepLink);
    AnalyticsService.episodeShareCopy({
      episodeId,
      location: { ...referrer, button: EVENT_CONSTANTS.BUTTON.SHARE },
    });
    SnackbarUtils.success('Link copied to clipboard');
  };

  return (
    <EpisodeControlButtonsComponent
      compliance={compliance}
      isUserAnonymous={!userId}
      isUserInternal={isUserInternal}
      isEpisodeRedeemRequired={isEpisodeRedeemRequired}
      isEpisodePremium={!!(isFreeTrialUser && episode.isPremium)}
      episodeId={episodeId}
      episodeStatus={episodeStatus}
      newFeatureAlert={newFeatureAlert}
      episodeDeepLink={episode.deepLink}
      onRedeem={onRedeem}
      onPlayNow={onPlayNow}
      isBookmarked={isBookmarked}
      isBookmarking={isBookmarking}
      onToggleBookmark={() => {
        if (!isBookmarking) {
          onToggleBookmark();
        }
      }}
      onTrailer={onTrailer}
      onTakeAssessment={onTakeAssessment}
      onGiveFeedback={onGiveFeedback}
      onShare={onShare}
      onCopyLink={onCopyLink}
      isPlaying={isEpisodePlaying && activeChapterId !== episode.trailerId}
      isPreviewing={isEpisodePlaying && activeChapterId === episode.trailerId}
    />
  );
};
